// @ts-nocheck

import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Gavel, TrendingUp, AlertCircle, CheckCircle, Clock, Eye, ExternalLink, Zap, Edit, Trash2, Plus, Minus } from 'lucide-react';
import { getNextMinimumBid, getBidIncrementSafe, validateAndCorrectBid } from '@/utils/bidValidation';
import { submitBidOptimized } from '@/utils/bidHelpers';
import { formatCurrency } from '@/lib/utils';
import { motion } from 'framer-motion';

interface Bid {
  id: string;
  lotNumber: string;
  auctionTitle: string;
  artworkTitle: string;
  artworkImage: string;
  bidAmount: number;
  currentBid: number;
  status: 'winning' | 'outbid' | 'won' | 'lost';
  bidTime: string;
  endTime: string;
}

const MyBidsTab: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState<'bids' | 'proxy'>('bids');
  const [activeFilter, setActiveFilter] = useState('all');

  const { data: bids = [], isLoading } = useQuery({
    queryKey: ['userBids', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      // Try RPC first
      const rpc = await supabase.rpc('get_user_bids', { p_user_id: user.id });
      if (!rpc.error && rpc.data) {
        return (rpc.data as any[]).map((row: any) => {
          const isClosed = ['completed', 'closed', 'ended'].includes(String(row.auction_status || '').toLowerCase());
          const ui_status = !isClosed
            ? (row.is_outbid ? 'outbid' : 'winning')
            : (row.is_outbid ? 'lost' : 'won');
          return { ...row, ui_status };
        });
      }

      // Fallback to direct select with mapping if RPC unavailable
      const { data, error } = await supabase
        .from('bids')
        .select(`
          id,
          amount,
          status,
          created_at,
          lot:lots(
            id,
            lot_number,
            current_bid,
            auction:auctions(title, status),
            artwork:artworks(title, image_url)
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error fetching user bids (fallback):', error);
        return [];
      }
      return (data || []).map((row: any) => {
        const is_outbid = (row.lot?.current_bid ?? row.amount) > row.amount;
        const auction_status = row.lot?.auction?.status || 'active';
        const isClosed = ['completed', 'closed', 'ended'].includes(String(auction_status).toLowerCase());
        const ui_status = !isClosed ? (is_outbid ? 'outbid' : 'winning') : (is_outbid ? 'lost' : 'won');
        return {
          bid_id: row.id,
          lot_id: row.lot?.id,
          lot_number: row.lot?.lot_number,
          auction_title: row.lot?.auction?.title,
          artwork_title: row.lot?.artwork?.title,
          artwork_image_url: row.lot?.artwork?.image_url,
          bid_amount: row.amount,
          current_bid: row.lot?.current_bid,
          bid_created_at: row.created_at,
          is_outbid,
          auction_status,
          ui_status,
        };
      });
    },
    enabled: !!user?.id,
  });

  // Proxy bids
  const { data: proxyBids = [], isLoading: proxyLoading } = useQuery({
    queryKey: ['userProxyBids', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const rpc = await supabase.rpc('get_user_proxy_bids', { p_user_id: user.id });
      if (!rpc.error && rpc.data) return rpc.data;

      // Fallback to direct select with relationships
      const { data, error } = await supabase
        .from('proxy_bids')
        .select(`
          id,
          lot_id,
          max_amount,
          active,
          created_at,
          lots!inner(
            id,
            lot_number,
            current_bid,
            starting_bid,
            auctions(id, title, status),
            artworks(title, image_url)
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) {
        console.error('Error fetching proxy bids (fallback):', error);
        return [];
      }
      return (data || []).map((row: any) => ({
        proxy_id: row.id,
        lot_id: row.lot_id || row.lots?.id,
        lot_number: row.lots?.lot_number,
        auction_id: row.lots?.auctions?.id,
        auction_title: row.lots?.auctions?.title,
        auction_status: row.lots?.auctions?.status,
        artwork_title: row.lots?.artworks?.title,
        artwork_image_url: row.lots?.artworks?.image_url,
        max_amount: row.max_amount,
        active: row.active,
        current_bid: row.lots?.current_bid,
        starting_bid: row.lots?.starting_bid,
        created_at: row.created_at,
      }));
    },
    enabled: !!user?.id,
  });

  const updateProxy = useMutation({
    mutationFn: async ({ proxy_id, fields }: { proxy_id: string; fields: any }) => {
      if (!user?.id) return;
      const { error } = await supabase.from('proxy_bids').update(fields).eq('id', proxy_id).eq('user_id', user.id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['userProxyBids'] })
  });

  const deleteProxy = useMutation({
    mutationFn: async (proxy_id: string) => {
      if (!user?.id) return;
      const { error } = await supabase.from('proxy_bids').delete().eq('id', proxy_id).eq('user_id', user.id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['userProxyBids'] })
  });

  // Inline edit state for proxy bids (amount aligned to increment grid)
  const [proxyEdit, setProxyEdit] = useState<Record<string, { open: boolean; amount: number; min: number; inc: number; loading: boolean }>>({});

  const beginEditProxy = async (pb: any) => {
    try {
      setProxyEdit(prev => ({ ...prev, [pb.proxy_id]: { open: true, amount: pb.max_amount, min: pb.max_amount, inc: 0, loading: true } }));
      const minimum = await getNextMinimumBid(pb.lot_id);
      const baseAmount = pb.current_bid || pb.starting_bid || minimum;
      const increment = await getBidIncrementSafe(Number(baseAmount), pb.auction_id);
      const k = Math.max(0, Math.ceil((Number(pb.max_amount) - Number(minimum)) / Number(increment)));
      const snapped = Number(minimum) + k * Number(increment);
      setProxyEdit(prev => ({ ...prev, [pb.proxy_id]: { open: true, amount: snapped, min: Number(minimum), inc: Number(increment), loading: false } }));
    } catch (e) {
      console.error('Failed to initialize proxy edit', e);
      setProxyEdit(prev => ({ ...prev, [pb.proxy_id]: { open: false, amount: pb.max_amount, min: pb.max_amount, inc: 0, loading: false } }));
    }
  };

  const changeEditAmount = (proxy_id: string, dir: 'up' | 'down') => {
    setProxyEdit(prev => {
      const st = prev[proxy_id];
      if (!st || st.loading) return prev;
      const next = dir === 'up' ? st.amount + st.inc : Math.max(st.min, st.amount - st.inc);
      return { ...prev, [proxy_id]: { ...st, amount: next } };
    });
  };

  const saveEditProxy = async (pb: any) => {
    const st = proxyEdit[pb.proxy_id];
    if (!st || st.loading) return;
    try {
      // Validate using server-side rules (rounding if needed)
      const validation = await validateAndCorrectBid(pb.lot_id, st.amount);
      const finalAmount = Math.max(st.min, validation.correctedAmount || st.amount);
      await updateProxy.mutateAsync({ proxy_id: pb.proxy_id, fields: { max_amount: finalAmount } });
      setProxyEdit(prev => ({ ...prev, [pb.proxy_id]: { ...st, open: false } }));
    } catch (e) {
      console.error('Failed to save proxy edit', e);
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'winning': 
        return { 
          color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30', 
          gradient: 'from-emerald-500/10 to-emerald-600/10',
          glow: 'shadow-emerald-500/20'
        };
      case 'outbid': 
        return { 
          color: 'bg-red-500/20 text-red-300 border-red-500/30', 
          gradient: 'from-red-500/10 to-red-600/10',
          glow: 'shadow-red-500/20'
        };
      case 'won': 
        return { 
          color: 'bg-blue-500/20 text-blue-300 border-blue-500/30', 
          gradient: 'from-blue-500/10 to-blue-600/10',
          glow: 'shadow-blue-500/20'
        };
      case 'lost': 
        return { 
          color: 'bg-gray-500/20 text-gray-300 border-gray-500/30', 
          gradient: 'from-gray-500/10 to-gray-600/10',
          glow: 'shadow-gray-500/20'
        };
      default: 
        return { 
          color: 'bg-gray-500/20 text-gray-300 border-gray-500/30', 
          gradient: 'from-gray-500/10 to-gray-600/10',
          glow: 'shadow-gray-500/20'
        };
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'winning': return <TrendingUp className="h-4 w-4" />;
      case 'outbid': return <AlertCircle className="h-4 w-4" />;
      case 'won': return <CheckCircle className="h-4 w-4" />;
      case 'lost': return <Clock className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const filteredBids = Array.isArray(bids) ? bids.filter((bid: any) => {
    if (activeFilter === 'all') return true;
    return (bid.ui_status || bid.bid_status) === activeFilter;
  }) : [];

  return (
    <ScrollArea className="h-full">
      <div className="p-8 space-y-8">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-transparent to-blue-500/10 rounded-3xl blur-xl" />
          <Card className="relative bg-white/5 backdrop-blur-xl border-white/10 rounded-3xl shadow-2xl">
            <CardHeader className="pb-8">
              <CardTitle className="flex items-center gap-4 text-2xl font-light text-white">
                <div className="p-4 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-2xl backdrop-blur-sm border border-white/20">
                  <Gavel className="h-8 w-8" />
                </div>
                <div>
                  <h2 className="text-2xl font-light tracking-tight">My Bids</h2>
                  <p className="text-sm text-white/60 font-normal mt-1">Track your auction participation</p>
                </div>
              </CardTitle>
            </CardHeader>
          </Card>
        </motion.div>

        {/* Section Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Tabs value={activeSection} onValueChange={(v: any) => setActiveSection(v)}>
            <TabsList className="grid w-full grid-cols-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-2 mb-4">
              <TabsTrigger value="bids" className="rounded-xl text-white/70 data-[state=active]:text-white data-[state=active]:bg-white/20 data-[state=active]:shadow-lg transition-all">
                Bids
              </TabsTrigger>
              <TabsTrigger value="proxy" className="rounded-xl text-white/70 data-[state=active]:text-white data-[state=active]:bg-white/20 data-[state=active]:shadow-lg transition-all">
                Proxy Bids
              </TabsTrigger>
            </TabsList>

            {/* Filter Tabs for Bids */}
            <TabsContent value="bids">
              <Tabs value={activeFilter} onValueChange={setActiveFilter}>
                <TabsList className="grid w-full grid-cols-5 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-2">
              <TabsTrigger 
                value="all" 
                className="rounded-xl text-white/70 data-[state=active]:text-white data-[state=active]:bg-white/20 data-[state=active]:shadow-lg transition-all"
              >
                All Bids
              </TabsTrigger>
              <TabsTrigger 
                value="winning" 
                className="rounded-xl text-white/70 data-[state=active]:text-white data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500/30 data-[state=active]:to-emerald-600/20 transition-all"
              >
                Winning
              </TabsTrigger>
              <TabsTrigger 
                value="outbid" 
                className="rounded-xl text-white/70 data-[state=active]:text-white data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500/30 data-[state=active]:to-red-600/20 transition-all"
              >
                Outbid
              </TabsTrigger>
              <TabsTrigger 
                value="won" 
                className="rounded-xl text-white/70 data-[state=active]:text-white data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500/30 data-[state=active]:to-blue-600/20 transition-all"
              >
                Won
              </TabsTrigger>
              <TabsTrigger 
                value="lost" 
                className="rounded-xl text-white/70 data-[state=active]:text-white data-[state=active]:bg-gradient-to-r data-[state=active]:from-gray-500/30 data-[state=active]:to-gray-600/20 transition-all"
              >
                Lost
              </TabsTrigger>
                </TabsList>
              </Tabs>
            </TabsContent>

            {/* Proxy Bids Content */}
            <TabsContent value="proxy">
              {proxyLoading ? (
                <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-3xl">
                  <CardContent className="py-12 text-center text-white/60">Loading your proxy bids...</CardContent>
                </Card>
              ) : (proxyBids as any[]).length === 0 ? (
                <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-3xl">
                  <CardContent className="py-12 text-center text-white/60">No proxy bids yet.</CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {(proxyBids as any[]).map((pb: any) => (
                    <Card key={pb.proxy_id} className="bg-white/5 backdrop-blur-xl border-white/10 rounded-3xl">
                      <CardContent className="p-6 flex gap-4 items-start">
                        <div className="w-20 h-20 rounded-xl overflow-hidden bg-white/10 border border-white/20 flex items-center justify-center">
                          {pb.artwork_image_url ? (
                            <img src={pb.artwork_image_url} alt={pb.artwork_title} className="w-full h-full object-cover" />
                          ) : (
                            <Gavel className="h-6 w-6 text-white/50" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="text-white font-medium truncate">{pb.artwork_title || 'Artwork'}</h4>
                              <p className="text-white/60 text-sm truncate">Lot #{pb.lot_number} • {pb.auction_title}</p>
                            </div>
                            <Badge className={`px-3 py-1 rounded-xl ${pb.auction_status === 'active' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : 'bg-gray-500/20 text-gray-300 border-gray-500/30'}`}>{pb.auction_status === 'active' ? 'Active' : 'Closed'}</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-6 mt-4 text-white">
                            <div>
                              <p className="text-xs text-white/50">Max Amount</p>
                              <p className="text-lg font-semibold">{formatCurrency(pb.max_amount)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-white/50">Current Bid</p>
                              <p className="text-lg font-semibold">{pb.current_bid ? formatCurrency(pb.current_bid) : '—'}</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Inline increment editor */}
                          {proxyEdit[pb.proxy_id]?.open ? (
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="icon"
                                className="bg-white/10 text-white border-white/20 hover:bg-white/20 rounded-xl"
                                onClick={() => changeEditAmount(pb.proxy_id, 'down')}
                              >
                                <Minus className="h-4 w-4" />
                              </Button>
                              <div className="px-3 py-1 rounded-xl border border-white/20 text-white bg-white/10 min-w-[120px] text-center">
                                {formatCurrency(proxyEdit[pb.proxy_id]?.amount || pb.max_amount)}
                              </div>
                              <Button
                                variant="outline"
                                size="icon"
                                className="bg-white/10 text-white border-white/20 hover:bg-white/20 rounded-xl"
                                onClick={() => changeEditAmount(pb.proxy_id, 'up')}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="bg-white/10 text-white border-white/20 hover:bg-white/20 rounded-xl"
                                onClick={() => saveEditProxy(pb)}
                              >
                                Save
                              </Button>
                            </div>
                          ) : (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="bg-white/10 text-white border-white/20 hover:bg-white/20 rounded-xl"
                                onClick={() => beginEditProxy(pb)}
                              >
                                <Edit className="h-4 w-4 mr-1" /> Edit
                              </Button>
                              {pb.auction_status === 'active' && (
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  className="rounded-xl"
                                  onClick={() => {
                                    if (confirm('Remove this proxy bid?')) deleteProxy.mutate(pb.proxy_id);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" /> Remove
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </motion.div>

        {/* Bids Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {activeSection === 'proxy' ? null : isLoading ? (
            <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-3xl">
              <CardContent className="py-24">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white/20 mx-auto"></div>
                  <p className="text-white/60 mt-4">Loading your bids...</p>
                </div>
              </CardContent>
            </Card>
          ) : filteredBids.length === 0 ? (
            <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-3xl">
              <CardContent className="py-24">
                <div className="text-center">
                  <div className="p-6 bg-white/5 rounded-full w-fit mx-auto mb-6">
                    <Gavel className="h-16 w-16 text-white/30" />
                  </div>
                  <h3 className="text-xl font-light text-white mb-2">No bids found</h3>
                  <p className="text-white/60 max-w-sm mx-auto">
                    {activeFilter === 'all' 
                      ? 'Start bidding on lots to see them here'
                      : `No ${activeFilter} bids at the moment`
                    }
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredBids.map((bid: any, index: number) => {
                const statusKey = (bid.ui_status || bid.bid_status) as string;
                const statusConfig = getStatusConfig(statusKey);
                return (
                  <motion.div
                    key={bid.bid_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className={`group bg-white/5 backdrop-blur-xl border-white/10 rounded-3xl hover:bg-white/10 transition-all duration-300 hover:shadow-2xl hover:${statusConfig.glow}`}>
                      <CardContent className="p-8">
                        <div className="flex gap-6">
                          {/* Artwork Image with Status Glow */}
                          <div className="relative flex-shrink-0">
                            <div className={`absolute inset-0 bg-gradient-to-br ${statusConfig.gradient} rounded-2xl blur-lg opacity-50`} />
                            <img 
                              src={bid.artwork_image_url || 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400'} 
                              alt={bid.artwork_title || 'Artwork'}
                              className="relative w-28 h-28 object-cover rounded-2xl border border-white/20 shadow-xl"
                            />
                          </div>

                          {/* Bid Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-6">
                              <div className="space-y-2">
                                <h3 className="text-xl font-semibold text-white group-hover:text-white/90 transition-colors">
                                  {bid.artwork_title || 'Untitled'}
                                </h3>
                                <p className="text-white/70 font-medium">{bid.auction_title}</p>
                                <p className="text-sm text-white/50 uppercase tracking-wider font-medium">
                                  LOT #{bid.lot_number}
                                </p>
                              </div>
                              <Badge className={`${statusConfig.color} px-4 py-2 rounded-xl flex items-center gap-2 font-medium backdrop-blur-sm`}>
                                {getStatusIcon(statusKey)}
                                <span className="capitalize">{statusKey}</span>
                              </Badge>
                            </div>

                            {/* Bid Amounts Grid */}
                            <div className="grid grid-cols-2 gap-8 mb-6">
                              <div className="space-y-2">
                                <p className="text-xs text-white/50 uppercase tracking-wider font-medium">Your Bid</p>
                                <div className="flex items-center gap-2">
                                  <Zap className="h-5 w-5 text-emerald-400" />
                                  <p className="text-2xl font-bold text-white">{formatCurrency(bid.bid_amount)}</p>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <p className="text-xs text-white/50 uppercase tracking-wider font-medium">Current Bid</p>
                                <div className="flex items-center gap-2">
                                  <TrendingUp className="h-5 w-5 text-blue-400" />
                                  <p className="text-2xl font-bold text-white">{formatCurrency(bid.current_bid || bid.bid_amount)}</p>
                                </div>
                              </div>
                            </div>

                            {/* Actions and Meta */}
                            <div className="flex items-center justify-between pt-6 border-t border-white/10">
                              <div className="space-y-1">
                                <p className="text-xs text-white/50 flex items-center gap-2">
                                  <Clock className="h-3 w-3" />
                                  Bid placed: {new Date(bid.bid_created_at).toLocaleDateString()}
                                </p>
                                <p className="text-xs text-white/50">
                                  Status: {bid.auction_status}
                                </p>
                              </div>
                              <div className="flex gap-3">
                                {bid.is_outbid && (
                                  <Button 
                                    size="sm" 
                                    className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-lg hover:shadow-emerald-500/25 rounded-xl"
                                    onClick={async () => {
                                      try {
                                        const minNext = await getNextMinimumBid(bid.lot_id);
                                        const inc = await getBidIncrementSafe(bid.current_bid || minNext, undefined);
                                        const candidate = Math.max(minNext, (bid.current_bid || 0) + inc);
                                        await submitBidOptimized({
                                          lotId: bid.lot_id,
                                          userId: user!.id,
                                          amount: candidate,
                                          onSuccess: () => queryClient.invalidateQueries({ queryKey: ['userBids'] })
                                        });
                                      } catch (e) {
                                        console.error('Quick re-bid failed', e);
                                      }
                                    }}
                                  >
                                    <Gavel className="h-4 w-4 mr-2" />
                                    Bid Again
                                  </Button>
                                )}
                                {bid.bid_status === 'won' && (
                                  <Button 
                                    size="sm" 
                                    className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white shadow-lg hover:shadow-blue-500/25 rounded-xl"
                                  >
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    View Details
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>
    </ScrollArea>
  );
};

export default MyBidsTab;